import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { rid } from "../../utils/shipinhao/utils";
import { ShipinhaoCheckLinkValidateParamsSchema } from "./schema";

export type ShipinhaoCheckLinkValidateParams = z.infer<typeof ShipinhaoCheckLinkValidateParamsSchema>;
export { ShipinhaoCheckLinkValidateParamsSchema } from "./schema";

export interface ShipinhaoCheckLinkValidateResponse {
    errCode: number;
    errMsg: string;
    data?: {
        baseResp: {
            errcode: number;
            errmsg: string;
        };
        title?: string;
    };
}

export type ShipinhaoCheckLinkValidateAction = CommonAction<
    ShipinhaoCheckLinkValidateParams,
    {
        title?: string;
        errMsg?: string;
        errCode?: number;
    }
>;

export const shipinhaoCheckLinkValidate: ShipinhaoCheckLinkValidateAction = async (_task, params) => {
    console.log("视频号验证链接参数", JSON.stringify(params));

    // 验证 cookies
    if (!params.cookies || params.cookies.length === 0) {
        return response(414, "视频号验证链接失败：缺少必需的 cookies 参数", {});
    }

    // 验证 extraParam 是否存在
    if (!params.extraParam) {
        return response(400, "缺少 extraParam 参数", {});
    }

    if (!params.extraParam.fingerPrintDeviceId || !params.extraParam.aId || !params.extraParam.uin) {
        return response(400, "fingerPrintDeviceId、aId和uin不能为空", {});
    }

    // 验证链接参数
    if (!params.link) {
        return response(400, "link 参数不能为空", {});
    }

    const headers = {
        cookie: params.cookies.map((it: { name: string; value: string }) => `${it.name}=${it.value}`).join(";"),
        referer: "https://channels.weixin.qq.com/",
        origin: "https://channels.weixin.qq.com/",
        "user-agent":
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36",
        "finger-print-device-id": params.extraParam.fingerPrintDeviceId,
        "x-wechat-uin": params.extraParam.uin,
    };

    console.log("视频号验证链接 headers", JSON.stringify(headers));
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.localIP, params.accountId];
    const http = new Http(...args);

    const GetTimeStamp = ($: number) => ($ ? Date.now().toString().substring(0, $) : Date.now().toString());

    // 视频号助手验证链接参数
    const linkParams: Record<string, string | number | null> = {
        timestamp: GetTimeStamp(13),
        pluginSessionId: null,
        rawKeyBuff: null,
        reqScene: 7,
        scene: 7,
        url: params.link,
        _log_finder_id: params.extraParam.finderUserName || "",
        _log_finder_uin: "",
    };

    _task.logger.info(`视频号验证链接参数: ${params.link}`);

    const queryData = {
        _aid: params.extraParam?.aId || "",
        _rid: rid(),
        _pageUrl: "https://channels.weixin.qq.com/micro/content/post/finderNewLifeCreate",
    };
    const queryString = new URLSearchParams(queryData).toString();

    console.log(
        "视频号验证链接请求",
        `https://channels.weixin.qq.com/micro/content/cgi-bin/mmfinderassistant-bin/helper/helper_parse_mp_link?${queryString}`,
    );

    // 调用视频号助手验证链接API
    const res = await http.api<ShipinhaoCheckLinkValidateResponse>(
        {
            method: "post",
            url: `https://channels.weixin.qq.com/micro/content/cgi-bin/mmfinderassistant-bin/helper/helper_parse_mp_link?${queryString}`,
            data: linkParams,
            headers: {
                ...headers,
                "Content-Type": "application/json",
            },
        },
        { retries: 3, retryDelay: 20, timeout: 5000 },
    );

    console.log("视频号验证链接响应", JSON.stringify(res));
    _task.logger.info("视频号验证链接响应:", { ...res });

    const isSuccess = res.errCode === 0;
    const baseResp = res.data?.baseResp;
    const message = `视频号验证链接${res.data?.title ? "成功" : `失败，原因：${baseResp?.errmsg || "无法添加此链接"}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;

    const data: {
        title?: string;
        errMsg?: string;
        errCode?: number;
    } = {
        title: res?.data?.title || "", // 校验成功会返回title
        errMsg: baseResp?.errmsg || "",
        errCode: baseResp?.errcode,
    };

    return response(isSuccess ? 0 : 414, message, data);
};
